import * as Sentry from '@sentry/react'
import './app.css'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProvider } from '@/context/AuthContext'
import { MaintenanceProvider } from '@/context/MaintenanceContext'
import { ProfileProvider } from '@/context/ProfileContext'
import { RewardProvider } from '@/context/RewardContext'
import { ImageProvider } from '@/context/ImageContext'
import { NotificationAlertProvider } from '@/context/NotificationAlertContext'
import { AnnouncementProvider } from '@/context/AnnouncementContext'
import { CookieConsentProvider } from '@/components/cookiesPolicy'
import AppRoutes from '@/routes/routes'
import MaintenanceRoute from '@/routes/MaintenanceRoute'
// import { ToastContainer } from 'react-toastify'
// import 'react-toastify/dist/ReactToastify.css'
import { Toaster } from 'sonner'
import { useEffect } from 'react'
import sessionTracker from '@/utils/sessionTracker'
import { AccessibleImages } from '@/components/accessibility/Accessibility'
import { TabNav } from '@/components/tabNavigation'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/queryClient'
import { GTMTestPanel } from '@/components/gtm'
import '@/utils/gtmDebug' // Import GTM debug utilities
import MaintenanceDebug from '@/components/maintenance/MaintenanceDebug' // Debug enabled

function App() {
  useEffect(() => {
    // Cleanup session tracking when app unmounts
    return () => {
      sessionTracker.endSession()
    }
  }, [])

  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <MaintenanceProvider>
          <ProfileProvider>
            <RewardProvider>
            <ImageProvider>
              {/* <NotificationAlertProvider> */}
              <AnnouncementProvider>
                <TabNav addSkipLink={true} autoEnhance={true}>
                  <AccessibleImages>
                    <CookieConsentProvider
                      routeIntegrationOptions={{
                        autoShowSettingsOnPrivacyPages: true,
                        privacyRoutes: [
                          '/privacy-policy',
                          '/cookie-policy',
                          '/data-rights',
                          '/terms-of-service'
                        ]
                      }}
                      gtmOptions={{ enabled: true }}
                    >
                      <MaintenanceRoute>
                        <Sentry.ErrorBoundary
                          fallback={
                            <div style={{ padding: '2rem', textAlign: 'center' }}>
                              <h2>Something went wrong</h2>
                              <p>An unexpected error occurred. Please refresh the page.</p>
                              <button
                                type='button'
                                onClick={() => window.location.reload()}
                                style={{
                                  marginTop: '1rem',
                                  padding: '8px 20px',
                                  fontSize: '0.95rem',
                                  borderRadius: '8px',
                                  border: 'none',
                                  background: '#111827',
                                  color: '#fff',
                                  cursor: 'pointer'
                                }}
                              >
                                Reload page
                              </button>
                            </div>
                          }
                        >
                          <AppRoutes />
                        </Sentry.ErrorBoundary>
                      </MaintenanceRoute>

                      {/* GTM Test Panel - Development Only (inside CookieConsentProvider) */}
                      {/* <GTMTestPanel /> */}
                      {/* Debug maintenance mode */}

                      {/* <MaintenanceDebug /> */}
                    </CookieConsentProvider>
                  </AccessibleImages>
                </TabNav>
                <Toaster
                  position='top-right'
                  duration={4000}
                  expand={true}
                  richColors={false}
                  toastOptions={{
                    style: {
                      padding: '20px 24px',
                      fontSize: '15px',
                      lineHeight: '1.5',
                      minWidth: '320px',
                      maxWidth: '420px',
                      background: 'white', // transparent white
                      backdropFilter: 'blur(20px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(20px) saturate(180%)', // Safari support
                      border: '1px solid rgba(255, 255, 255, 0.3)', // thin glass border
                      borderRadius: '16px', // rounded corners
                      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)' // soft shadow
                    }
                  }}
                />
              </AnnouncementProvider>
              {/* </NotificationAlertProvider> */}
            </ImageProvider>
            </RewardProvider>
          </ProfileProvider>
        </MaintenanceProvider>
      </AuthProvider>
    </QueryClientProvider>
    </HelmetProvider>
  )
}

export default App
